import styled, { keyframes } from 'styled-components';

// Animações suaves
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f6f8fb;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  z-index: 2000;
  padding: 0;
  animation: ${fadeIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  height: 100dvh;
  overflow: hidden;

  @media (max-width: 1024px) {
    background: #ffffff;
  }
`;

export const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 0;
  max-width: none;
  width: 100vw;
  height: 100dvh;
  max-height: none;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  box-shadow: none;
  overflow: hidden;
  border: none;

  @media (max-width: 1024px) {
    width: 100vw;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  height: 72px;
  padding: 0 24px;
  background: #ffffff;
  color: var(--ink);
  flex-shrink: 0;
  position: relative;
  border-bottom: 1px solid var(--line);
  box-sizing: border-box;

  @media (max-width: 768px) {
    height: auto;
    min-height: 60px;
    padding: 10px 16px;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

export const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  background: var(--brand-50);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .material-icons {
    font-size: 22px;
    color: var(--brand-700);
  }
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ModalTitle = styled.h2`
  font-family: var(--font-sans);
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ModalSubtitle = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: var(--text-muted);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  color: var(--text);
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: #f3f6fa;
    color: var(--ink);
  }

  .material-icons {
    font-size: 22px;
  }
`;

/* Corpo do editor: rail de seções à esquerda, formulário à direita. */
export const EditorLayout = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

/* Rail de seções (redesign V3): agrupado no desktop, fita horizontal no celular. */
export const TabsContainer = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 240px;
  flex-shrink: 0;
  box-sizing: border-box;
  background: #ffffff;
  border-right: 1px solid var(--line);
  padding: 10px 12px 16px;
  overflow-y: auto;

  @media (max-width: 1024px) {
    flex-direction: row;
    align-items: center;
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid var(--line);
    padding: 8px 12px;
    gap: 4px;
    overflow-x: auto;
    overflow-y: hidden;

    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #bdc1c6;
      border-radius: 3px;
    }
  }
`;

export const TabGroupTitle = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: #80868b;
  text-transform: uppercase;
  padding: 14px 12px 6px;

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const Tab = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 12px;
  border: none;
  background: ${(props) => (props.$active ? "var(--brand-50)" : "transparent")};
  color: ${(props) => (props.$active ? "var(--brand-700)" : "var(--text)")};
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 10px;
  white-space: nowrap;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${(props) => (props.$active ? "var(--brand-50)" : "#f3f6fa")};
    color: ${(props) => (props.$active ? "var(--brand-700)" : "var(--ink)")};
  }

  .material-icons {
    font-size: 20px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 0;
    font-size: 0;
    gap: 0;
    width: 48px;
    height: 48px;
    justify-content: center;

    .material-icons {
      font-size: 26px;
    }
  }
`;

export const ModalBody = styled.div`
  padding: 24px 32px 48px;
  overflow-y: auto !important;
  overflow-x: hidden;
  flex: 1 1 auto;
  background: var(--bg);
  min-height: 0;
  position: relative;
  max-height: none;

  @media (max-width: 768px) {
    padding: 20px 16px;
    max-height: none;
  }

  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #F7F9FC;
    border-radius: 10px;
    margin: 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #bdc1c6;
    border-radius: 10px;
    border: 3px solid #F7F9FC;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9aa0a6;
  }

  /* Melhoria para textos longos */
  p, span, div {
    word-break: break-word;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  @media (max-width: 1024px) {
    padding: 28px 24px;

    &::-webkit-scrollbar {
      width: 8px;
    }
  }

  @media (max-width: 768px) {
    padding: 20px 16px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      border: 2px solid #F7F9FC;
    }
  }
`;

export const FormSection = styled.div`
  max-width: 1040px;
  min-height: min-content;
  padding: 32px;
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  box-sizing: border-box;

  & + & {
    margin-top: 24px;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 24px;
  }
  @media (max-width: 768px) {
    padding: 20px 16px;
    border-radius: 12px;
  }
`;

export const SectionTitle = styled.h3`
  font-family: var(--font-sans);
  font-size: 22px;
  font-weight: 500;
  color: var(--ink);
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin: 0 0 18px 0;
  }
`;

/** Texto de apoio logo abaixo do título da seção. */
export const SectionHint = styled.p`
  font-size: 12px;
  line-height: 17px;
  color: var(--text-muted);
  margin: -18px 0 24px 0;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 20px;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  margin-bottom: 24px;

  ${FormGrid} & {
    margin-bottom: 0;
  }
`;

export const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: var(--text);
  text-transform: uppercase;
  margin: 0;

  .material-icons {
    font-size: 18px;
    color: var(--text-muted);
  }
`;

export const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  font-size: 15px;
  color: var(--ink);
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  font-family: inherit;
  background: #ffffff;

  &:hover {
    border-color: #9aa0a6;
  }
  &:focus {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 1px var(--brand);
  }
  &:disabled {
    background: #f1f3f4;
    color: #80868b;
    cursor: not-allowed;
    border-color: var(--line);
  }
  &::placeholder {
    color: #9aa0a6;
    font-weight: 400;
  }
`;

export const Select = styled.select`
  width: 100%;
  height: 48px;
  padding: 0 40px 0 14px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  font-size: 15px;
  color: var(--ink);
  background: #ffffff
    url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%235F6368'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")
    no-repeat right 12px center;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  font-family: inherit;

  &:hover {
    border-color: #9aa0a6;
  }
  &:focus {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 1px var(--brand);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 12px 14px;
  border: 1px solid #dadce0;
  border-radius: 12px;
  font-size: 15px;
  line-height: 22px;
  color: var(--ink);
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  font-family: inherit;
  background: #ffffff;
  resize: vertical;

  &:hover {
    border-color: #9aa0a6;
  }
  &:focus {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 1px var(--brand);
  }
  &::placeholder {
    color: #9aa0a6;
  }
`;

export const CharCounter = styled.div<{ $overLimit?: boolean }>`
  text-align: right;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => props.$overLimit ? '#D93025' : '#5f6368'};
  transition: color 0.3s ease;
`;

export const StatCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  background: #F7F9FC;
  border-radius: 16px;
  margin-bottom: 16px;
  border: 2px solid #f1f3f4;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: #E2E6EA;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 500;
  color: #5f6368;

  .material-icons {
    font-size: 24px;
    color: #1264C7;
  }
`;

export const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1264C7;
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 48px;
  border-top: 1px solid #e0e0e0;
  background: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 -1px 8px rgba(15, 23, 42, 0.06);
  position: relative;
  z-index: 10;

  @media (max-width: 1024px) {
    padding: 18px 24px;
    gap: 12px;
  }

  @media (max-width: 768px) {
    padding: 16px;
    flex-direction: column;
    gap: 12px;
    /* position: sticky; removido pois o layout flex já garante o footer no final */
    /* bottom: 0; */
  }
`;

export const FooterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #5f6368;
  font-size: 13px;
  font-weight: 500;
  
  .material-icons {
    font-size: 20px;
    color: #1264C7;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    display: none; /* Oculta em mobile para economizar espaço */
  }
`;

export const SaveFeedback = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 520px;
  padding: 10px 12px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fef2f2;
  color: #991b1b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;

  .material-icons {
    color: #dc2626;
    font-size: 18px;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    gap: 8px;
  }
`;

export const Button = styled.button<{ $secondary?: boolean }>`
  padding: 14px 32px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:hover::before {
    width: 400px;
    height: 400px;
  }

  .material-icons {
    font-size: 20px;
    position: relative;
    z-index: 1;
  }

  span:not(.material-icons) {
    position: relative;
    z-index: 1;
  }

  ${(props) => props.$secondary ? `
    background: white;
    color: #5f6368;
    border: 2px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    &:hover {
      border-color: #bdc1c6;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }
  ` : `
    background: #1264C7;
    color: white;
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);

    &:hover {
      background: #1264C7;
      box-shadow: 0 4px 12px rgba(66, 133, 244, 0.4);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  `}

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
    border-radius: 10px;
    gap: 8px;
    flex: 1;
    justify-content: center;

    .material-icons {
      font-size: 18px;
    }

    &:hover::before {
      width: 0;
      height: 0;
    }

    ${(props) => !props.$secondary && `
      &:hover {
        transform: none;
        box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
      }
    `}

    ${(props) => props.$secondary && `
      &:hover {
        transform: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      }
    `}
  }
`;


/* ---------- Horários (redesign V3): dia | aberto | intervalos ---------- */
export const HoursToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: -8px 0 16px;
`;

export const HoursChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 0;
  background: var(--brand-50);
  color: var(--brand-700);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--brand-100);
  }
  .material-icons {
    font-size: 16px;
  }
`;

export const HoursHeader = styled.div`
  display: grid;
  grid-template-columns: 150px 60px minmax(0, 1fr);
  gap: 16px;
  padding: 0 0 6px;
  border-bottom: 1px solid var(--line);
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: var(--text);
  text-transform: uppercase;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const HoursDayRow = styled.div<{ $closed?: boolean }>`
  display: grid;
  grid-template-columns: 150px 60px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  min-height: 60px;
  padding: 10px 0;
  border-bottom: 1px solid #eef1f4;

  .day {
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 500;
    color: ${(p) => (p.$closed ? "#9aa0a6" : "var(--ink)")};
  }
  .slots {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .closed {
    font-size: 13px;
    color: #9aa0a6;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 44px;
    .slots {
      grid-column: 1 / -1;
    }
  }
`;

export const HoursSwitch = styled.button<{ $on: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: ${(p) => (p.$on ? "var(--brand)" : "#bdc1c6")};
  position: relative;
  border: 0;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s;

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: ${(p) => (p.$on ? "18px" : "2px")};
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: #fff;
    transition: left 0.2s;
  }
`;

export const HoursSlot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 4px 0 10px;
  border-radius: 10px;
  border: 1px solid #dadce0;
  background: #fff;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  color: var(--ink);

  &:hover,
  &:focus-within {
    border-color: var(--brand);
  }
  input[type="time"] {
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    color: inherit;
    width: 72px;
    padding: 0;
  }
  .x {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    border: 0;
    background: transparent;
    color: var(--text-muted);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }
  .x:hover {
    background: var(--error-bg);
    color: #c5221f;
  }
  .x .material-icons {
    font-size: 16px;
  }
`;

export const AddSlotButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px dashed #c2d7f5;
  background: #fff;
  color: var(--brand);
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #f8fafd;
  }
  .material-icons {
    font-size: 16px;
  }
`;
